import * as React from "react"
import { Popover as PopoverPrimitive } from "@base-ui/react/popover"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/primitives/drawer"
import { useOverlayForm } from "@/design-system/use-overlay-form.ts"
import { cn } from "@/primitives/utils"

/*
 * beacon: a popover chooses its form as it opens (`useOverlayForm`) and keeps it while open. On the
 * desk it is anchored to its trigger, as below. On `compact` it is a bottom sheet, as tall as its
 * content, so a Picker or a small form sits above the keyboard rather than under it; the caller's
 * width gives way to the sheet's, and its padding and gaps are kept. `anchored` keeps it anchored
 * at every width, for what reads in place and is gone at a glance (a chart's marker, opened on a
 * hover). Callers don't change: the Trigger, Title and Description follow the form. A popover with
 * no PopoverTitle of its own passes `title` to its content, which names the sheet under its handle
 * and is its accessible name; the anchored form shows none.
 */
const PopoverFormContext = React.createContext<"popover" | "sheet">("popover")

function Popover({
  anchored = false,
  ...props
}: PopoverPrimitive.Root.Props & { anchored?: boolean }) {
  const { docked, onOpenChange } = useOverlayForm(props, !anchored)
  return (
    <PopoverFormContext value={docked ? "sheet" : "popover"}>
      {docked ? (
        // The sheet closes for reasons a popover has no word for (a swipe, Android's Back), which
        // reach the caller's handler as Popover's details: callers read `open`, not why.
        <Drawer
          data-slot="popover"
          {...(props as React.ComponentProps<typeof Drawer>)}
          onOpenChange={onOpenChange as React.ComponentProps<typeof Drawer>["onOpenChange"]}
        />
      ) : (
        <PopoverPrimitive.Root data-slot="popover" {...props} onOpenChange={onOpenChange} />
      )}
    </PopoverFormContext>
  )
}

function PopoverTrigger({
  openOnHover,
  delay,
  closeDelay,
  ...props
}: PopoverPrimitive.Trigger.Props) {
  // beacon: a sheet opens on a press alone; what a hover would open is the anchored form's.
  if (React.use(PopoverFormContext) === "sheet")
    return (
      <DrawerTrigger
        data-slot="popover-trigger"
        {...(props as React.ComponentProps<typeof DrawerTrigger>)}
      />
    )
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      openOnHover={openOnHover}
      delay={delay}
      closeDelay={closeDelay}
      {...props}
    />
  )
}

function PopoverContent({
  className,
  align = "center",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  // beacon: keeps 8px clear of the viewport's edge unless the caller asks for more.
  collisionPadding = 8,
  collisionAvoidance,
  title,
  titleEnd,
  children,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "collisionPadding" | "collisionAvoidance"
  > & {
    /** beacon: what the popover holds, as "Status" or "Link a ticket": the sheet's title. */
    title?: string
    /** beacon: what ends the sheet's title row, as a multi-pick's Done; none when anchored. */
    titleEnd?: React.ReactNode
  }) {
  // beacon: on `compact`, a bottom sheet holding the popover's column at the popover's padding (or
  // the caller's), at the sheet's width rather than the caller's, under its `title`.
  if (React.use(PopoverFormContext) === "sheet")
    return (
      <DrawerContent
        data-slot="popover-content"
        {...(props as Omit<React.ComponentProps<typeof DrawerContent>, "className" | "children">)}
      >
        {title && (
          <DrawerHeader
            className={cn("px-4 pb-2", titleEnd && "flex-row items-center justify-between")}
          >
            <DrawerTitle>{title}</DrawerTitle>
            {titleEnd}
          </DrawerHeader>
        )}
        <div
          className={cn(
            "flex flex-col gap-3 p-4",
            typeof className === "string" && className,
            "w-auto max-w-none min-w-0",
          )}
        >
          {children}
        </div>
      </DrawerContent>
    )
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        {...(collisionAvoidance && { collisionAvoidance })}
        className="isolate z-50"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            // beacon: at least the trigger's width and otherwise its content's (the caller sets a
            // width where one is wanted), in UI text, lifted by the popover shadow whose hairline
            // replaces the ring, entering over 150ms on the snappy curve.
            "z-50 flex max-w-(--available-width) min-w-(--anchor-width) origin-(--transform-origin) flex-col gap-3 rounded-lg bg-popover p-3 text-ui text-popover-foreground shadow-popover outline-hidden duration-150 ease-snappy data-[side=bottom]:slide-in-from-top-1 data-[side=inline-end]:slide-in-from-left-1 data-[side=inline-start]:slide-in-from-right-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-[0.98] data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0 data-closed:zoom-out-[0.98]",
            className,
          )}
          {...props}
        >
          {children}
        </PopoverPrimitive.Popup>
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  )
}

function PopoverHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="popover-header"
      // beacon: UI text.
      className={cn("flex flex-col gap-1 text-ui", className)}
      {...props}
    />
  )
}

function PopoverTitle({ className, ...props }: PopoverPrimitive.Title.Props) {
  // beacon: in a sheet, the title names the drawer, whose parts are Dialog's.
  const Title =
    React.use(PopoverFormContext) === "sheet" ? DialogPrimitive.Title : PopoverPrimitive.Title
  return (
    <Title
      data-slot="popover-title"
      // beacon: a title in the type scale.
      className={cn("text-title font-semibold", className)}
      {...(props as DialogPrimitive.Title.Props)}
    />
  )
}

function PopoverDescription({ className, ...props }: PopoverPrimitive.Description.Props) {
  const Description =
    React.use(PopoverFormContext) === "sheet"
      ? DialogPrimitive.Description
      : PopoverPrimitive.Description
  return (
    <Description
      data-slot="popover-description"
      className={cn("text-muted-foreground", className)}
      {...(props as DialogPrimitive.Description.Props)}
    />
  )
}

export { Popover, PopoverContent, PopoverDescription, PopoverHeader, PopoverTitle, PopoverTrigger }
